import {
  MODIFIED_AVIA_ROUTE_LEG_LOGS,
  SCS_MODIFIED_LOG_VALUE,
} from '@lp/features/RoutePlanner/components/LogisticsChangeLog/consts';
import {
  ChangeLogValues,
  convertGroundLogLocation,
  getChangeLogDateTimeString,
  getResourceModifiedLogValue,
  ModifiedLogValue,
  prepareChangedValues,
} from '@lp/features/RoutePlanner/components/LogisticsChangeLog/utils';
import {
  AccommodationLeg,
  AircraftLogResourceChanges,
  AirportLogLocation,
  BaseRouteLegData,
  CrewLogResourceChanges,
  CsLogResourceChanges,
  CustomAddressLogLocation,
  CustomCrewMember,
  LegLocationTypeEnum,
  LogisticsChangeLogType,
  LogisticsProviderChangesLog,
  LogisticsResourceModifiedLogType,
  ModifiedGroundLogLocation,
  ModifiedResourceTypeEnum,
  NonTmdxGroundLeg,
  OcsConsoleLogResourceChanges,
  SLogResourceChanges,
  SupplementalLiftAviaLeg,
  TmdxDriveAffiliateGroundLeg,
} from '@/api/apis/route';
import { getFullName, getLocationAddress } from '@/utils/helpers';

const getTextChangedValue = (text?: string | null): ModifiedLogValue | null => {
  if (!text) return null;

  return {
    type: 'text',
    title: text,
  };
};

const getUserChangedValue = (text?: string | null, imageUrl?: string): ModifiedLogValue | null => {
  if (!text) return null;

  return {
    type: 'user',
    title: text,
    imageUrl,
  };
};

export const getLocationString = (
  location: CustomAddressLogLocation | AirportLogLocation | ModifiedGroundLogLocation | null,
  logType?: LogisticsChangeLogType,
): string | null => {
  if (!location) return null;

  const locationData =
    logType && MODIFIED_AVIA_ROUTE_LEG_LOGS.includes(logType)
      ? (location as AirportLogLocation)
      : convertGroundLogLocation(location as ModifiedGroundLogLocation)!;

  switch (locationData.locationType) {
    case LegLocationTypeEnum.airport:
      const airportCode = locationData.iata || locationData.icao;
      if (locationData.fboLocation?.address) {
        const addressLine = `${locationData.fboLocation.address.addressLine1 || locationData.fboLocation.address.addressLine2}, ${locationData.fboLocation.address.city}`;
        return `FBO ${airportCode}, ${addressLine}`;
      }

      return airportCode;
    case LegLocationTypeEnum.hub:
      return `${locationData.name} (${locationData.code})`;
    case LegLocationTypeEnum.hospital:
      return `DH, ${locationData.city}, ${locationData.state}, ${locationData.country}`;
    case LegLocationTypeEnum.txcenter:
      return `TXC, ${locationData.city}, ${locationData.state}, ${locationData.country}`;
    case LegLocationTypeEnum.homeResidence:
    case LegLocationTypeEnum.custom:
    default:
      return getLocationAddress(locationData.location);
  }
};

export const convertLocationToModifiedValue = (
  data: CustomAddressLogLocation | AirportLogLocation | ModifiedGroundLogLocation | null,
  logType?: LogisticsChangeLogType,
): ModifiedLogValue | null => {
  const locationString = getLocationString(data, logType);

  return getTextChangedValue(locationString);
};

export const convertDateTimeToString = (
  dateTime: string,
  timeZone: string,
  formatString = 'd MMM yyyy, HH:mm zzz',
): ModifiedLogValue | null => {
  const dateTimeString = getChangeLogDateTimeString(dateTime, formatString, timeZone);

  return getTextChangedValue(dateTimeString);
};

export const convertPrimitiveToString = (
  oldData: Record<string, string | number> | null,
  newData: Record<string, string | number> | null,
): ChangeLogValues<ModifiedLogValue | null>[] => {
  const prevData = oldData ? Object.values(oldData).join(', ').trim() : null;
  const nextData = newData ? Object.values(newData).join(', ').trim() : null;

  return [
    {
      leftValue: getTextChangedValue(prevData),
      rightValue: getTextChangedValue(nextData),
    },
  ];
};

export const convertLocationChangedValues =
  <T extends 'departureLocation' | 'arrivalLocation'>(fieldName: T) =>
  (
    oldData: Pick<BaseRouteLegData<AirportLogLocation | ModifiedGroundLogLocation>, T> | null,
    newData: Pick<BaseRouteLegData<AirportLogLocation | ModifiedGroundLogLocation>, T> | null,
    logType: LogisticsChangeLogType,
  ): ChangeLogValues<ModifiedLogValue | null>[] => {
    return [
      {
        leftValue: oldData ? convertLocationToModifiedValue(oldData[fieldName], logType) : null,
        rightValue: newData ? convertLocationToModifiedValue(newData[fieldName], logType) : null,
      },
    ];
  };

export const convertHotelLocationChangedValues = (
  oldData: Pick<AccommodationLeg<CustomAddressLogLocation>, 'location'> | null,
  newData: Pick<AccommodationLeg<CustomAddressLogLocation>, 'location'> | null,
): ChangeLogValues<ModifiedLogValue | null>[] => {
  return [
    {
      leftValue: oldData ? convertLocationToModifiedValue(oldData.location) : null,
      rightValue: newData ? convertLocationToModifiedValue(newData.location) : null,
    },
  ];
};

export const convertDateTimeChangedValues =
  <T extends 'departureTime' | 'arrivalTime', R extends 'departureLocalTimezone' | 'arrivalLocalTimezone'>(
    dateTimeField: T,
    timeZoneField: R,
  ) =>
  (
    oldData: Pick<BaseRouteLegData, T | R>,
    newData: Pick<BaseRouteLegData, T | R>,
  ): ChangeLogValues<ModifiedLogValue | null>[] => {
    return [
      {
        leftValue: convertDateTimeToString(oldData[dateTimeField], oldData[timeZoneField]),
        rightValue: convertDateTimeToString(newData[dateTimeField], newData[timeZoneField]),
      },
    ];
  };

export const convertHotelDateTimeChangedValues =
  <T extends 'checkInAt' | 'checkOutAt'>(dateTimeField: T) =>
  (
    oldData: Pick<AccommodationLeg, T>,
    newData: Pick<AccommodationLeg, T>,
  ): ChangeLogValues<ModifiedLogValue | null>[] => {
    return [
      {
        leftValue: convertDateTimeToString(oldData[dateTimeField], 'America/New_York', 'd MMM yyyy'),
        rightValue: convertDateTimeToString(newData[dateTimeField], 'America/New_York', 'd MMM yyyy'),
      },
    ];
  };

const filterResourcesLogs = (
  list: LogisticsResourceModifiedLogType[],
): (CsLogResourceChanges | SLogResourceChanges | CrewLogResourceChanges | OcsConsoleLogResourceChanges)[] =>
  list.filter(
    (i): i is CsLogResourceChanges | SLogResourceChanges | CrewLogResourceChanges | OcsConsoleLogResourceChanges =>
      i.$type !== ModifiedResourceTypeEnum.AircraftLogResourceChanges,
  );

export const convertResourcesToValue = (
  oldData: Pick<BaseRouteLegData<unknown, LogisticsResourceModifiedLogType>, 'resources' | 'hasScs'>,
  newData: Pick<BaseRouteLegData<unknown, LogisticsResourceModifiedLogType>, 'resources' | 'hasScs'>,
): ChangeLogValues<ModifiedLogValue | null>[] => {
  const oldValues = prepareChangedValues<
    BaseRouteLegData<unknown, LogisticsResourceModifiedLogType>,
    'resources' | 'hasScs'
  >(oldData, newData);
  const newValues = prepareChangedValues<
    BaseRouteLegData<unknown, LogisticsResourceModifiedLogType>,
    'resources' | 'hasScs'
  >(newData, oldData);

  const scsChanges = {
    leftValue: oldValues.hasScs ? SCS_MODIFIED_LOG_VALUE : null,
    rightValue: newValues.hasScs ? SCS_MODIFIED_LOG_VALUE : null,
  };

  const rightResources = filterResourcesLogs(newValues.resources as LogisticsResourceModifiedLogType[]);
  const resourcesChanges = filterResourcesLogs(oldValues.resources as LogisticsResourceModifiedLogType[]).map(
    (r, index) => {
      const rightResource = rightResources[index];

      return {
        leftValue: r ? getResourceModifiedLogValue(r) : null,
        rightValue: rightResource ? getResourceModifiedLogValue(rightResource) : null,
      };
    },
  );

  const hasSCSChanges = scsChanges.leftValue || scsChanges.rightValue;

  return [...resourcesChanges, ...(hasSCSChanges ? [scsChanges] : [])];
};

export const convertAircraftResourcesToValue = (
  oldData: Pick<BaseRouteLegData<unknown, LogisticsResourceModifiedLogType>, 'resources'>,
  newData: Pick<BaseRouteLegData<unknown, LogisticsResourceModifiedLogType>, 'resources'>,
): ChangeLogValues<ModifiedLogValue | null>[] => {
  const oldValues = prepareChangedValues<BaseRouteLegData<unknown, LogisticsResourceModifiedLogType>, 'resources'>(
    oldData,
    newData,
  );
  const newValues = prepareChangedValues<BaseRouteLegData<unknown, LogisticsResourceModifiedLogType>, 'resources'>(
    newData,
    oldData,
  );

  const newFilteredResources = newValues.resources.filter(
    (i): i is AircraftLogResourceChanges =>
      i.$type === ModifiedResourceTypeEnum.AircraftLogResourceChanges && !!i.resourceId,
  );
  return oldValues.resources
    .filter(
      (i): i is AircraftLogResourceChanges =>
        i.$type === ModifiedResourceTypeEnum.AircraftLogResourceChanges && !!i.resourceId,
    )
    .map((r, index) => {
      const rightResource = newFilteredResources[index];

      return {
        leftValue: getResourceModifiedLogValue(r),
        rightValue: getResourceModifiedLogValue(rightResource),
      };
    });
};

const getCrewMemberString = (crewMember: CustomCrewMember) => {
  const fullName = getFullName(crewMember.firstName, crewMember.lastName);
  const phoneNumber = crewMember.phoneNumber ? ` (${crewMember.phoneNumber})` : '';

  return `${fullName}${phoneNumber}`.trim();
};

export const convertCrewMemberToValue =
  <T extends 'firstCrewMember' | 'secondCrewMember'>(fieldName: T) =>
  (
    oldData: Pick<SupplementalLiftAviaLeg<unknown, LogisticsResourceModifiedLogType>, T> | null,
    newData: Pick<SupplementalLiftAviaLeg<unknown, LogisticsResourceModifiedLogType>, T> | null,
  ): ChangeLogValues<ModifiedLogValue | null>[] => {
    const oldMember = oldData ? getCrewMemberString(oldData[fieldName]) : null;
    const newMember = newData ? getCrewMemberString(newData[fieldName]) : null;

    return [
      {
        leftValue: getUserChangedValue(oldMember),
        rightValue: getUserChangedValue(newMember),
      },
    ];
  };

export const convertPriceChangesToValue = (
  oldData: Pick<SupplementalLiftAviaLeg<unknown, LogisticsResourceModifiedLogType>, 'priceInCents'> | null,
  newData: Pick<SupplementalLiftAviaLeg<unknown, LogisticsResourceModifiedLogType>, 'priceInCents'> | null,
): ChangeLogValues<ModifiedLogValue | null>[] => {
  return [
    {
      leftValue: oldData ? getTextChangedValue((oldData.priceInCents / 100).toFixed(2)) : null,
      rightValue: newData ? getTextChangedValue((newData.priceInCents / 100).toFixed(2)) : null,
    },
  ];
};

export const convertProviderChangesToValue = (
  oldData: { provider: LogisticsProviderChangesLog } | null,
  newData: { provider: LogisticsProviderChangesLog } | null,
): ChangeLogValues<ModifiedLogValue | null>[] => {
  return [
    {
      leftValue: getTextChangedValue(oldData?.provider.name),
      rightValue: getTextChangedValue(newData?.provider.name),
    },
  ];
};

export const convertDriverChangesToValue = (
  oldData: Pick<NonTmdxGroundLeg<unknown, LogisticsResourceModifiedLogType>, 'driver' | 'phoneNumber'> | null,
  newData: Pick<NonTmdxGroundLeg<unknown, LogisticsResourceModifiedLogType>, 'driver' | 'phoneNumber'> | null,
): ChangeLogValues<ModifiedLogValue | null>[] => {
  const prevPhoneNumber = oldData?.phoneNumber ? ` (${oldData.phoneNumber})` : '';
  const prevValue = oldData ? `${oldData.driver}${prevPhoneNumber}`.trim() : null;

  const newPhoneNumber = newData?.phoneNumber ? ` (${newData.phoneNumber})` : '';
  const nextValue = newData ? `${newData.driver}${newPhoneNumber}`.trim() : null;

  return [
    {
      leftValue: getUserChangedValue(prevValue),
      rightValue: getUserChangedValue(nextValue),
    },
  ];
};

export const convertReassignedTripReference = (
  oldData: Pick<TmdxDriveAffiliateGroundLeg, 'tripReference'>,
  newData: Pick<TmdxDriveAffiliateGroundLeg, 'tripReference'>,
): ChangeLogValues<ModifiedLogValue | null>[] => {
  return [
    {
      leftValue: getTextChangedValue(oldData?.tripReference?.tripNumber),
      rightValue: getTextChangedValue(newData?.tripReference?.tripNumber),
    },
  ];
};
